// Per-platform Prophet chunk limit, measured in CHARACTERS.
//
// Both platforms count characters (not bytes) for outgoing message length:
// Instagram's real cap is ~1000 characters and Telegram's is 4096 characters.
// Our SAFE limits leave headroom under those platform caps so our gates fire
// BEFORE the instagram/telegram-server splitter safety-net would re-cut a
// chunk (which silently turns one rate-limit slot into several Meta calls).
//
// The concrete limits are RUNTIME-configurable: the backend serves them at
// GET {apiUrl}/config and the app pushes them into `limitsStore` at start via
// setChunkLimits(). Until that resolves (or if it fails) the built-in
// defaults below are used.

export type ChunkPlatform = 'instagram' | 'telegram';

// Fallback limits (in characters) used before the runtime /config response
// arrives, or if the request fails.
const DEFAULT_LIMITS: Record<ChunkPlatform, number> = {
  instagram: 900,
  telegram: 990,
};

// Mutable in-memory store of the effective limits. Seeded with the defaults
// and overwritten once by setChunkLimits() from the /config response.
const limitsStore: Record<ChunkPlatform, number> = { ...DEFAULT_LIMITS };

// Runtime shape returned by GET {apiUrl}/config.
export type MessageLengthConfig = {
  instMaxChars: number;
  tgMaxChars: number;
  /** Prophet's default per-page msg/min limit (PAGE_RATE_LIMIT) — for hints in the limits UI. */
  prophetRateLimitPerMinute?: number;
};

// Update the in-memory limits from a runtime /config response. Invalid or
// missing fields are ignored so the corresponding default stays in effect.
export function setChunkLimits(config: Partial<MessageLengthConfig>): void {
  if (typeof config.instMaxChars === 'number' && config.instMaxChars > 0) {
    limitsStore.instagram = config.instMaxChars;
  }
  if (typeof config.tgMaxChars === 'number' && config.tgMaxChars > 0) {
    limitsStore.telegram = config.tgMaxChars;
  }
}

export type ChunkUsage = {
  current: number; // current size in `unit`
  limit: number; // max allowed in `unit`
  unit: 'chars';
  exceedsLimit: boolean;
};

export function getChunkUsage(text: string, platform: ChunkPlatform): ChunkUsage {
  const current = text.length;
  const limit = limitsStore[platform];
  return {
    current,
    limit,
    unit: 'chars',
    exceedsLimit: current > limit,
  };
}

// Convenience helper for validators that just want a boolean answer.
export function exceedsChunkLimit(text: string, platform: ChunkPlatform): boolean {
  return getChunkUsage(text, platform).exceedsLimit;
}
